import { AxiosError, AxiosResponse } from "axios";
import { IAppointment, IValidationResult } from "lobbie";
import { useCallback, useEffect, useState } from "react";
import { DEFAULT_ERROR_MESSAGE } from "src/constants";
import { useAxiosGetter } from "src/hooks/useAxios";
import { handleError, isFailedRequest, notify } from "src/utils";

export const usePatientAppointment = (appointmentId: number | string | undefined) => {
    const getter = useAxiosGetter();
    const [appointment, setAppointment] = useState<IAppointment>();
    const [isLoadingAppointment, setLoadingAppointment] = useState<boolean>(false);

    const getAppointment = useCallback(() => {
        setLoadingAppointment(true);
        if (!appointmentId) return;

        getter(`/patient/appointment/${appointmentId}`)
            .then((response: AxiosResponse | void) => {
                setLoadingAppointment(false);
                const result = response && (response.data as IValidationResult | IAppointment);
                if (!result) {
                    response?.status &&
                        response.status > 399 &&
                        notify({
                            level: "error",
                            title: "Error getting appointment.",
                            message: DEFAULT_ERROR_MESSAGE,
                        });
                } else if (isFailedRequest(result as IValidationResult)) {
                    notify({
                        level: "error",
                        title: "Failed to get appointment.",
                        message: (result as IValidationResult)?.message || DEFAULT_ERROR_MESSAGE,
                    });
                } else {
                    setAppointment(result as IAppointment);
                }
            })
            .catch((error: AxiosError) => {
                setLoadingAppointment(false);
                handleError(error);
            });
    }, [getter, appointmentId, setAppointment]);

    useEffect(() => {
        getAppointment();
    }, [getAppointment]);

    return {
        appointment,
        setAppointment,
        getAppointment,
        isLoadingAppointment,
    };
};
